import { styled, useTheme } from 'styled-components';
import { useTranslation } from '@packages/utils';
import { Flex } from 'antd';
import { Text } from '@idaho-aeyc/ui-kit';
import useGetFormData from '../../../RegistrationModal/RegistrationModalForm/ChooseLanguage/ChooseLanguage.data';
import { chooseLanguageSecionKey } from '../../../RegistrationModal/RegistrationModal.data';
import BackNextButtons from '../BackNextButtons';
import { TRegistrationFormDataUI } from '../../../../../../data/types/events.types';

// Language Form Styled Components
const ChooseLanguageWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;

  @media (max-width: 820px) {
    height: calc(80vh - 120px);
  }
`;

const AttendanceButton = styled.button<{
  $active: boolean;
  $disabled?: boolean;
  theme: any;
}>`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colorBlackMainBorder};
  font-size: 16px;
  cursor: pointer;
  width: 100%;
  border-radius: 8px;
  height: 100%;
  font-weight: 500;
  transition: all 0.2s;
  padding: 2px 50px;

  @media (max-width: 768px) {
    padding: 0px 8px;
    margin-top: 8px;
    white-space: nowrap;
    height: auto;
    min-height: 40px;
  }

  ${props =>
    props.$disabled &&
    `
      cursor: not-allowed;
      opacity: 0.5;
      `}

  &:first-child {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  &:last-child {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  ${props =>
    props.$active &&
    `
      background: ${props.theme.buttonColorPrimaryNormalBg};
      border-color: ${props.theme.buttonColorPrimaryNormalBg};
      color: white;
      transition: all 0.2s;
    `}
`;

const StyledFlex = styled(Flex)`
  width: 100%;

  @media (max-width: 768px) {
    max-width: 400px;
  }
`;

const ChooseLanguage = ({ data }: { data?: TRegistrationFormDataUI }) => {
  const theme = useTheme();
  const { chooseLanguageData } = useGetFormData();
  const { userLanguage } = chooseLanguageData;
  const { i18n } = useTranslation({
    keyPrefix: chooseLanguageSecionKey,
    lng: 'translate',
  });

  // Separate translation hook without keyPrefix for enums
  const { t: tEnums } = useTranslation({ lng: 'translate' });

  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language);
  };

  const formatEventType = (type: string) => {
    return tEnums(`enums.event-type.${type}`, { defaultValue: type });
  };

  // Get event title and type directly from data prop
  const eventTitle = data?.eventTitle || '';
  const eventType = data?.eventType || '';
  const displayTitle =
    eventType && eventTitle
      ? `${formatEventType(eventType)}  ·  ${eventTitle}`
      : formatEventType(eventType) || eventTitle;

  return (
    <>
      <ChooseLanguageWrapper data-cy="registration-choose-language">
        {/* Event Information Section */}
        {data && (
          <Flex
            vertical
            gap={8}
            style={{
              marginBottom: 32,
              width: '100%',
              textAlign: 'left',
            }}
          >
            {/* Event Type - Event Title */}
            {displayTitle && (
              <Text
                style={{
                  fontSize: '28px',
                  fontWeight: 700,
                  lineHeight: '1.2',
                }}
                data-cy="registration-event-title"
              >
                {displayTitle}
              </Text>
            )}

            {/* Location Address */}
            {data.locationAddress && (
              <Text
                style={{
                  fontSize: '16px',
                  fontWeight: 400,
                  color: '#666',
                }}
                data-cy="registration-location-address"
              >
                {data.locationAddress}
              </Text>
            )}

            {/* Hosted by */}
            {data.hostName && (
              <Text
                style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  marginTop: 16,
                }}
                data-cy="registration-hosted-by"
              >
                Hosted by: {data.hostName}
              </Text>
            )}
          </Flex>
        )}

        <p style={{ textAlign: 'left', width: '100%' }}>
          Which language would you like to use to fill out the registration
          form?
        </p>
        <p style={{ textAlign: 'left', width: '100%' }}>
          ¿Qué idioma le gustaría utilizar para completar el formulario de
          registro?
        </p>
        <StyledFlex>
          {userLanguage.options.map(option => (
            <AttendanceButton
              key={option.value}
              $active={i18n.language === option.value}
              onClick={() => handleLanguageChange(option.value)}
              theme={theme}
              data-cy="choose-language-button"
            >
              {option.label}
            </AttendanceButton>
          ))}
        </StyledFlex>
      </ChooseLanguageWrapper>
      <BackNextButtons />
    </>
  );
};

export default ChooseLanguage;
